import { ChapterContent } from '../../../types';
import { h, p, list, checklist, tip, warning, diagram } from '../blocks';

/**
 * Content Rewrite 3.0 — prose-first textbook for Ark Nova base game.
 * Verified values come from local PL/EN rule material and actionCardStrengths.ts.
 * Printed reputation-track bonuses remain blocked; do not invent them.
 */
export const arkNovaCoreChapters: ChapterContent[] = [
  {
    title: 'Cel gry',
    content: [
      warning(
        'Ta instrukcja uczy zweryfikowanych procedur gry podstawowej. Premie nadrukowane na torze reputacji (w tym pole 5) odczytuj z fizycznej planszy, dopóki nie pojawi się czytelny materiał. Nie zgaduj wartości z kart zoo, sponsorów ani projektów — obowiązuje ich nadruk.'
      ),
      h('Co robisz i po co', 2),
      p(
        'W Ark Nova budujesz nowoczesne zoo: wybiegi, zwierzęta, sponsorów, współpracę z uczelniami i ogrodami partnerskimi oraz projekty ochrony przyrody. Cel nie jest „jak największa atrakcyjność”. Wygrywa gracz z najwyższym wynikiem końcowym po skrzyżowaniu dwóch torów i punktacji końcowej.'
      ),
      h('Dwa tory wyniku', 2),
      p(
        'Atrakcyjność przesuwa się po torze atrakcyjności i daje dochód w przerwie. Ochrona przyrody przesuwa się z przeciwnej strony planszy. Gdy znaczniki jednego gracza znajdą się w tym samym obszarze punktowym albo miną się, uruchamia się koniec gry. Po ostatnich turach i punktacji wygrywa najwyższy wynik końcowy.'
      ),
      h('Mini-przykład przy stole', 2),
      p(
        'Masz wysoką atrakcyjność i prawie żadnej ochrony. Twój znacznik atrakcyjności jest blisko środka, ale ochrona stoi daleko. Przeciwnik z umiarkowaną atrakcyjnością i mocną ochroną może skrzyżować tory wcześniej i wygrać punktacją obszaru plus kartą końcową. Samochód pełen atrakcyjności bez ochrony rzadko wygrywa.'
      ),
      warning(
        'Typowy błąd: grać wyłącznie o atrakcyjność i traktować ochronę jak opcjonalny dodatek.'
      ),
      tip(
        'Wyjątek i odsyłacz: wartości obszarów punktowych odczytujesz z planszy w chwili punktowania (rozdział „Koniec gry”). Rytm jednej akcji na turę opisuje „Przebieg tury”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Cel: najwyższy wynik końcowy po skrzyżowaniu torów. P: Czy wystarczy wygrać atrakcyjnością? O: Nie. P: Czy ta instrukcja zastępuje tekst każdej karty zoo? O: Nie; obowiązuje nadruk karty.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Najważniejsze pojęcia',
    content: [
      h('Co musisz znać przed pierwszą akcją', 2),
      p(
        'Poniższe pojęcia wracają w każdej turze. Jeśli zrozumiesz mapę, pięć kart akcji, żetony X, ulepszenia, przerwę i reputację, reszta reguł układa się w jeden rytm.'
      ),
      h('Mapa zoo', 2),
      p(
        'Każdy gracz ma własną mapę heksową. Budujesz na niej standardowe wybiegi, pawilony, kioski, wybiegi specjalne oraz unikatowe budynki ze sponsorów. Budynek musi w całości leżeć na dozwolonych polach i nie może nachodzić na inne budynki ani pola niedostępne.'
      ),
      h('Pięć kart akcji i siła', 2),
      p(
        'Masz karty: Karty, Budowa, Zwierzęta, Stowarzyszenie, Sponsorzy. Leżą pod mapą na pozycjach 1–5. Numer pozycji to siła akcji — zwykle im dalej w prawo, tym silniej. Po użyciu karta wraca na pozycję 1, a karty po jej lewej przesuwają się o jedno miejsce w prawo.'
      ),
      h('Żetony X i ulepszenia', 2),
      p(
        'Żeton X zwiększa siłę wykonywanej akcji o 1. Siłę ustalasz przed rozpatrzeniem efektu; żetony zdobyte w tej samej akcji nie zwiększają jej. Limit posiadania wynosi 5 — nadmiar przepada. Ulepszenie odwraca jedną kartę na stronę II w tej samej pozycji; w typowej partii nie ulepszysz wszystkich pięciu.'
      ),
      h('Przerwa oraz reputacja', 2),
      p(
        'Gra nie ma rund. Znacznik przerwy wędruje po torze; po dojściu do końca przeprowadzacie przerwę. Reputacja wyznacza standardowy zasięg sześciopolowej wystawki: 0–1 → folder 1; 2–4 → 1–2; 5–7 → 1–3; 8–9 → 1–4; 10–14 → 1–5; 15 → 1–6. Wejście na 10+ wymaga Kart II. Na 15 znacznik zostaje; każda dalsza reputacja daje 1 atrakcyjność.'
      ),
      warning(
        'Typowy błąd: sprawdzać limit ręki po każdej turze albo brać kartę z wystawki poza zasięgiem tylko dlatego, że akcja jest ulepszona.'
      ),
      tip(
        'Wyjątek: „zgarnięcie” przy odpowiednio silnej akcji Kart bierze dowolną kartę z wystawki niezależnie od reputacji. Szczegóły w „Akcja Karty”; tabele sił 1–5 są w każdym rozdziale akcji.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Pozycja = siła; po akcji karta na 1; przerwa ≠ runda; reputacja = zasięg folderów. P: Czy mogę mieć 6 żetonów X? O: Nie, limit 5. P: Czy ulepszenie zmienia pozycję karty? O: Nie.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Przygotowanie gry podstawowej',
    content: [
      h('Co przygotowanie ustala i dlaczego', 2),
      p(
        'Setup ustawia tor przerwy, wystawkę, projekty ochrony i start każdego gracza. Błąd tutaj psuje pierwszą przerwę i pierwsze zagrania zwierząt. Dla pierwszej partii użyjcie identycznych map A.'
      ),
      h('Wspólny obszar', 2),
      checklist(
        'core-shared-setup',
        [
          'Połóż planszę główną i planszę stowarzyszenia.',
          'Ustaw znacznik przerwy na polu startowym odpowiadającym liczbie graczy.',
          'Potasuj żetony premii i umieść losowe odkryte żetony na pustych żółtych polach premii planszy. Pozostałe odłóż.',
          'Potasuj talię zoo.',
          'Utwórz zakrytą talię i rozdaj sześć zakrytych kart na pola wystawki. Odkryjesz je po zakończeniu przygotowania graczy.',
          'Przygotuj osobne stosy odrzuconych kart, pieniędzy, żetonów X, znaczników i budynków.',
          'Wylosuj bazowe projekty ochrony przyrody: 2 / 3 / 4 projekty dla 2 / 3 / 4 graczy.',
          'Umieść projekty nad planszą stowarzyszenia.',
          'Przygotuj dostępne ogrody partnerskie i uczelnie na planszy stowarzyszenia.',
        ],
        'Wspólne przygotowanie'
      ),
      h('Przygotowanie gracza', 2),
      checklist(
        'core-player-setup',
        [
          'Bierze mapę zoo i zestaw elementów w swoim kolorze.',
          'Ustawia znaczniki atrakcyjności, ochrony przyrody i reputacji na polach startowych.',
          'Kładzie czterech pracowników stowarzyszenia w przewidzianych miejscach na mapie; pierwszy dostępny pracownik znajduje się w aktywnym obszarze zgodnie z ikonografią mapy.',
          'Kładzie swoje znaczniki gracza na torach i przy mapie.',
          'Bierze pięć kart akcji.',
          'Umieszcza kartę Zwierzęta na pozycji 1, a cztery pozostałe losowo na pozycjach 2–5, stroną podstawową do góry.',
          'Dobiera 8 kart zoo, wybiera 4 na rękę, a pozostałe 4 odrzuca.',
          'Dobiera 2 karty punktacji końcowej i zachowuje je w tajemnicy.',
          'Otrzymuje pieniądze startowe zgodnie z kolejnością graczy wskazaną przez planszę/mapę.',
          'Na mapie A umieszcza startowy wybieg wielkości 3 na oznaczonych polach, pustą stroną do góry.',
          'Po przygotowaniu wszystkich graczy odkryj sześć kart wystawki.',
        ],
        'Przygotowanie gracza'
      ),
      h('Pierwsza partia', 2),
      checklist(
        'core-first-game',
        [
          'Użyjcie map A.',
          'Nie wybierajcie zaawansowanych map asymetrycznych.',
          'Przy pierwszej rozgrywce można skorzystać z przygotowanych talii startowych, jeżeli je posiadacie.',
          'Dodatek Zwierzęta Wodne najlepiej dołączyć od pierwszej partii tylko wtedy, gdy wszyscy gracze poznają grę z tej instrukcji. Moduł dodatku zawiera pełne zmiany przygotowania.',
        ],
        'Pierwsza partia'
      ),
      h('Przykład kontroli gotowości', 2),
      p(
        'Przed pierwszą turą: wystawka odkryta (6 kart), każdy ma Zwierzęta na pozycji 1 i startowy wybieg 3, znacznik przerwy stoi na polu dla waszej liczby graczy, a projekty ochrony leżą nad stowarzyszeniem.'
      ),
      warning(
        'Typowe błędy: odkrycie wystawki za wcześnie, brak startowego wybiegu, start na mapie asymetrycznej oraz użycie pola przerwy dla innej liczby graczy.'
      ),
      tip(
        'Wyjątek i odsyłacz: dokładne pieniądze startowe bierzesz z planszy/mapy według kolejności. Po setupie przejdź do „Przebieg tury”, a o mapie A — do atlasu map.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Gotowa partia ma wspólny stół, mapy A, ręce, karty akcji i odkrytą wystawkę. P: Ile projektów przy 3 graczach? O: Trzy. P: Czy karty akcji zaczynają stroną II? O: Nie.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Przebieg tury',
    content: [
      h('Co, kiedy i dlaczego', 2),
      p(
        'W swojej turze wykonujesz dokładnie jedną akcję. To cały rytm gry: nie ma faz „rundy budowy” ani „rundy zwierząt”. Siłę ustalasz przed efektem; dopiero potem przesuwasz karty i sprawdzasz przerwę oraz koniec gry.'
      ),
      checklist(
        'core-turn',
        [
          'Wybierz kartę akcji.',
          'Określ jej siłę na podstawie pozycji.',
          'Opcjonalnie wydaj żetony X.',
          'Wykonaj akcję.',
          'Rozpatrz wszystkie efekty wywołane przez akcję.',
          'Przenieś używaną kartę na pozycję 1, przesuwając pozostałe.',
          'Rozpatrz efekty „po zakończeniu akcji”.',
          'Uzupełnij wystawkę, jeżeli efekt tego wymaga.',
          'Sprawdź przerwę.',
          'Sprawdź koniec gry.',
        ],
        'Tura'
      ),
      diagram(
        'ark-action-slide',
        'Diagram przesuwania kart akcji na pozycje 1–5',
        'Po użyciu karty trafia na pozycję 1; karty na lewo przesuwają się o 1 w prawo. Nie wykonujesz drugiej akcji.'
      ),
      h('Przykład pełnego rytmu', 2),
      p(
        'Budowa leży na pozycji 3. Wydajesz jeden X → siła 4. Budujesz wybieg rozmiaru 3 (koszt 6 monet), bierzesz premię pola, odwracasz kartę Budowa na pozycję 1, a poprzednia pozycja 1 przesuwa się na 2. Dopiero potem sprawdzasz, czy znacznik przerwy doszedł do końca.'
      ),
      p(
        'Nie wolno wybrać zwykłej akcji i wykonać jej „za zero”. Gdy nie możesz albo nie chcesz wykonać tekstu akcji, użyj akcji zastępczej żetonu X (osobny rozdział).'
      ),
      warning(
        'Typowy błąd: wykonać drugą akcję „przy okazji” przesunięcia, wydać X w środku rozpatrywania albo pominąć przesunięcie kart.'
      ),
      tip(
        'Wyjątek: efekty „po zakończeniu akcji” rozpatrujesz po ułożeniu kart, nie przed. Limitu ręki nie sprawdzasz w turze — dopiero w przerwie.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Jedna akcja → efekt → karta na 1 → przerwa/koniec. P: Czy mogę wydać X zdobyty w tej akcji na tę samą akcję? O: Nie. P: Czy tura to cała runda? O: Nie; rund nie ma.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Akcja Karty',
    content: [
      h('Co robi akcja i po co', 2),
      p(
        'Akcja Karty zasila rękę z talii albo wystawki i zawsze przesuwa znacznik przerwy o 2. To główny sposób zdobywania zwierząt, sponsorów i projektów zanim je zagrasz innymi akcjami.'
      ),
      h('Tabela sił 1–5 (obie strony)', 2),
      list([
        'Siła 1 I: przerwa +2; dobierz 1 z talii, potem odrzuć 1 z ręki. II: przerwa +2; weź 1 z talii albo wystawki w zasięgu reputacji.',
        'Siła 2 I: przerwa +2; dobierz 1 z talii. II: przerwa +2; weź 2 łącznie z talii i/lub wystawki w zasięgu, potem odrzuć 1.',
        'Siła 3 I: przerwa +2; dobierz 2 z talii, potem odrzuć 1. II: przerwa +2; weź 2 z talii/zasięgu albo zgarnij 1 dowolną z wystawki.',
        'Siła 4 I: przerwa +2; dobierz 2 z talii. II: przerwa +2; weź 3 łącznie z talii i/lub wystawki w zasięgu.',
        'Siła 5 I: przerwa +2; dobierz 3 i odrzuć 1 albo zgarnij 1 dowolną z wystawki. II: przerwa +2; weź 4 z talii/zasięgu, potem odrzuć 1; albo zgarnij 1 dowolną.',
      ]),
      h('Zasięg i zgarnięcie', 2),
      diagram(
        'ark-reputation-reach',
        'Diagram reputacji i sześciu pozycji wystawki',
        'Wystawka ma 6 kart. 0–1: folder 1; 2–4: 1–2; 5–7: 1–3; 8–9: 1–4; 10–14: 1–5; 15: 1–6. Zgarnięcie omija zasięg.'
      ),
      p(
        'Dobieranie „w zasięgu reputacji” wybiera tylko foldery w twoim zakresie. Zgarnięcie zastępuje całe dobieranie i ignoruje reputację. Po zabraniu karty z wystawki uzupełnij puste miejsce na końcu tury. Limit ręki w trakcie tury możesz przekroczyć; sprawdzasz go w przerwie (zwykle 3; 5 po właściwej uczelni).'
      ),
      h('Pełny przykład przy stole', 2),
      p(
        'Masz reputację 4 (foldery 1–2) i Karty na pozycji 5, stroną I. Możesz dobrać 3 i odrzucić 1 albo zgarnąć dowolną z sześciu kart — także z folderu 6. Gdybyś miał stronę II i siłę 3, zamiast dobierania mógłbyś zgarnąć jedną kartę spoza zasięgu.'
      ),
      warning(
        'Typowy błąd: zgarnięcie przy sile 4 I albo branie folderu 3 przy reputacji 4 bez zgarnięcia / efektu omijającego zasięg.'
      ),
      tip(
        'Wyjątek: siła >5 nie poprawia tej tabeli Kart. Koszt folderu przy zagraniu z wystawki dotyczy Zwierząt II, Sponsorów II i projektu przez Stowarzyszenie II — nie samej akcji Karty.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Zawsze +2 przerwy; tabela zależy od siły i strony; zgarnięcie omija reputację. P: Czy limit ręki działa od razu po dobraniu? O: Nie, w przerwie. P: Czy uzupełniam wystawkę w środku akcji? O: Na końcu tury, o ile efekt nie mówi inaczej.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Akcja Budowa',
    content: [
      h('Co budujesz i po co', 2),
      p(
        'Budowa umieszcza budynki na mapie: standardowe wybiegi pod zwierzęta, kioski pod dochód, pawilony pod atrakcyjność oraz — po ulepszeniu — wybiegi specjalne. Bez legalnego wybiegu nie zagrasz zwierzęcia.'
      ),
      h('Siła i strony', 2),
      list([
        'Strona I: zbuduj dokładnie 1 budynek o rozmiarze ≤ siła; 2 monety za każde pole. Dostępne: kiosk, pawilon, standardowy wybieg i małe zoo.',
        'Strona II: zbuduj różne budynki o łącznym rozmiarze ≤ siła; ten sam koszt. II dodaje wolierę i terrarium; nie budujesz dwóch identycznych budynków w tej samej akcji.',
        'Unikatowe budynki zagrywa się tylko z właściwym sponsorem, nie zwykłą Budową.',
      ]),
      h('Zasady umieszczania', 2),
      diagram(
        'ark-build-legal',
        'Diagram legalnej i nielegalnej budowy',
        'Legalne: styk z wejściem / przyległość, rozmiar ≤ siła, 2 monety/pole. Nielegalne: bez styku, skała/woda, rozmiar > siła.'
      ),
      list([
        'Pierwszy budynek musi stykać się z krawędzią wejścia albo spełnić regułę startową mapy.',
        'Każdy kolejny musi przylegać co najmniej jednym bokiem do już zbudowanego budynku.',
        'Nie zakrywaj skał ani wody, chyba że efekt wyraźnie pozwala.',
        'Premie zakrytych pól bierzesz natychmiast.',
        'Budynku nie przesuwasz później bez efektu karty.',
        'Pusty wybieg leży stroną rozmiaru do góry; po zwierzęciu standardowy odwraca się na zajęty.',
      ]),
      h('Kiosk i pawilon', 2),
      p(
        'Pawilon po zbudowaniu daje wskazaną atrakcyjność. Kiosk daje dochód w przerwie: 1 monetę za każdy przylegający zajęty standardowy wybieg, pawilon lub kwalifikujący się specjalny/unikatowy budynek. Ten sam budynek może karmić kilka kiosków. Pusty standardowy wybieg nie daje dochodu.'
      ),
      h('Pełny przykład', 2),
      p(
        'Siła 5 II: budujesz wybieg 3 i kiosk 1 (łącznie 4 ≤ 5, różne budynki), płacisz 8 monet, bierzesz premię z zakrytego pola. Kontrprzykład: wybieg 4 przy sile 3 I jest nielegalny; budowa „w powietrzu” bez sąsiedztwa też.'
      ),
      warning(
        'Typowy błąd: liczenie pustego wybiegu do kiosku, budowa dwóch takich samych budynków na II albo zakrycie skały bez efektu.'
      ),
      tip(
        'Wyjątek i odsyłacz: geometria kafla pochodzi z fizycznego elementu — atlas map pomaga planować dziury i korytarze. Zwierzęta zajmują wybiegi w rozdziale „Akcja Zwierzęta”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'I = jeden budynek ≤ siła; II = różne, suma ≤ siła; 2 monety/pole. P: Czy mogę przesunąć budynek później? O: Nie bez efektu. P: Czy kiosk liczy puste wybiegi? O: Nie.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Akcja Zwierzęta',
    content: [
      h('Co robi akcja i kiedy', 2),
      p(
        'Zagrywasz zwierzęta z ręki, a po ulepszeniu także z wystawki w zasięgu reputacji. Każde zagranie wymaga spełnionych wymagań, wolnego właściwego wybiegu i zapłaty kosztu po zniżkach.'
      ),
      h('Tabela sił', 2),
      list([
        'Siła 1 (I i II): nie możesz zagrać zwierzęcia.',
        'Siła 2–4 I: najwyżej 1 zwierzę z ręki. II: 1 z ręki albo wystawki w zasięgu.',
        'Siła 5 I: najwyżej 2 zwierzęta z ręki. II: najpierw +1 reputacja, potem najwyżej 2 z ręki i/lub wystawki w zasięgu (nowy zasięg działa od razu).',
        'Za kartę z wystawki przez Zwierzęta II zapłać dodatkowo numer folderu. Dwa zwierzęta rozpatruj kolejno, kończąc pierwsze w pełni przed drugim.',
      ]),
      h('Procedura jednego zwierzęcia', 2),
      checklist(
        'core-play-animal',
        [
          'Wybierz kartę.',
          'Sprawdź, czy spełniasz wszystkie wymagania.',
          'Wybierz odpowiedni pusty wybieg.',
          'Sprawdź wymaganą wielkość i typ wybiegu.',
          'Zapłać koszt zwierzęcia.',
          'Zastosuj zniżki z ogrodów partnerskich i innych efektów.',
          'Umieść kartę przy swojej mapie.',
          'Zajmij wybieg: standardowy odwróć na stronę zajętą; specjalny oznacz zgodnie z pojemnością.',
          'Otrzymaj atrakcyjność z karty.',
          'Rozpatrz zdolność natychmiastową karty.',
          'Dodaj wszystkie ikony karty do swojego zoo.',
        ],
        'Zagrywanie zwierzęcia'
      ),
      h('Wymagania, koszt, wybiegi', 2),
      p(
        'Typowe wymagania: ikony kontynentu lub klasy, uczelnia, ogród, reputacja, ulepszona akcja, sąsiedztwo wody/skał, specjalny wybieg. Ikona właśnie zagrywanej karty nie spełnia jej własnych wymagań, chyba że tekst mówi inaczej. Za każdy pasujący ogród partnerski obniżasz koszt o 3 monety za każdą pasującą ikonę kontynentu na karcie; koszt nie spada poniżej zera.'
      ),
      p(
        'Standardowy wybieg: jeden pusty o wymaganym lub większym rozmiarze; nadmiar nie daje zwrotu. Specjalne (woliera, terrarium, małe zoo): zwierzę zajmuje pojemność zgodnie z kartą; kilka zwierząt może dzielić wybieg, dopóki starczy miejsc.'
      ),
      h('Pełny przykład', 2),
      p(
        'Masz siłę 3 I, ogród Europy, zwierzę europejskie za 9 monet z jedną ikoną Europy i wymaganiem wybiegu 3. Spełniasz wymagania bez własnej ikony karty, obniżasz koszt do 6, zajmujesz pusty wybieg 3, bierzesz atrakcyjność i zdolność. Kontrprzykład: zagranie do zajętego standardowego wybiegu albo liczenie własnej ikony jako wymagania.'
      ),
      warning(
        'Typowy błąd: zagranie przy sile 1, pominięcie dopłaty folderu z wystawki albo zniżka ogrodu z niepasującego kontynentu.'
      ),
      tip(
        'Wyjątek: II odblokowuje karty wymagające ikony Zwierzęta II. Słowa kluczowe zdolności — rozdział „Najczęstsze słowa kluczowe zwierząt”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Wymagania → wybieg → koszt → atrakcyjność → zdolność. P: Czy większy wybieg mieści mniejsze zwierzę? O: Tak; nadwyżka przepada. P: Czy standardowy wybieg mieści dwa zwierzęta? O: Nie.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Akcja Stowarzyszenie',
    content: [
      h('Co robi Stowarzyszenie', 2),
      p(
        'Wysyłasz dostępnego pracownika na planszę stowarzyszenia: reputacja, ogród partnerski, uczelnia albo wsparcie projektu ochrony. Pracownik wraca dopiero w przerwie. To główna droga do ochrony przyrody i zasięgu wystawki.'
      ),
      diagram(
        'ark-association',
        'Diagram akcji Stowarzyszenie',
        'Wybierz dostępnego pracownika, zadanie i legalną siłę. Pracownik wraca dopiero w przerwie.'
      ),
      h('Tabela sił i zadania', 2),
      list([
        'Siła 1 (I i II): brak zadania podstawowego o koszcie 1.',
        'Siła 2 I: dokładnie 1 zadanie — +2 reputacji. II: różne zadania o sumie ≤ 2; po zadaniu możesz 1 darowiznę.',
        'Siła 3 I: reputacja albo ogród. II: suma ≤ 3 + opcjonalna darowizna.',
        'Siła 4 I: reputacja, ogród albo uczelnia. II: suma ≤ 4 + darowizna.',
        'Siła 5 I: dowolne jedno zadanie, także wsparcie projektu. II: suma ≤ 5 + darowizna.',
        'Koszty zadań: reputacja = 2, ogród = 3, uczelnia = 4, wsparcie projektu = 5 (pracownik ×2, gdy twoje pole jest już zajęte). II: rodzaje zadań w jednej akcji muszą być różne; darowizna wymaga co najmniej 1 zadania.',
      ]),
      h('Ogrody, uczelnie, projekty', 2),
      p(
        'Ogród daje ikonę kontynentu i zniżkę −3 za pasującą ikonę kontynentu na zwierzęciu; kładziesz go na najniższym wolnym miejscu kolumny i bierzesz odkrytą premię. Uczelnia daje nadrukowane korzyści (nauka, reputacja, limit ręki itd.) w ten sam sposób.'
      ),
      checklist(
        'core-association-project',
        [
          'Wybierz projekt bazowy albo kartę projektu znajdującą się w grze.',
          'Wybierz poziom, którego wymaganie spełniasz.',
          'Połóż swój znacznik gracza na wolnym polu tego poziomu.',
          'Zdobądź wskazaną liczbę punktów ochrony przyrody.',
          'Otrzymaj ewentualną premię mapy odsłoniętą po zdjęciu znacznika.',
        ],
        'Akcja Stowarzyszenie — wsparcie projektu'
      ),
      p(
        'Każdy gracz wspiera dany projekt zwykle tylko raz; na poziomie stoi najwyżej jeden znacznik. Projekt z ręki musisz od razu wesprzeć. II pozwala zagrać projekt z wystawki w zasięgu — wtedy dopłać numer folderu. Darowizna (tylko II): zapłać najniższy wolny koszt, zyskaj 1 ochronę, ozn zajęte pole; kolejne darowizny drożeją.'
      ),
      h('Pełny przykład', 2),
      p(
        'Siła 5 II: wydajesz pracownika na +2 reputacji (koszt 2) i drugi na ogród (3). Potem możesz złożyć jedną darowiznę. Nie możesz dwa razy wziąć „reputacji” w tej samej akcji. Kontrprzykład: zajęcie poziomu już zajętego albo zagranie projektu bez możliwości wsparcia.'
      ),
      warning(
        'Typowy błąd: powtórzenie tego samego rodzaju zadania na II, darowizna bez zadania albo zapomnienie zwrotu pracowników w przerwie.'
      ),
      tip(
        'Wyjątek i odsyłacz: premie toru ochrony po zdobyciu punktów opisuje „Tory i premie”. Kolejność efektów kart — „Rodzaje efektów kart”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Pracownik → legalne zadanie ≤ siła → efekt; II łączy różne zadania. P: Czy mogę wesprzeć projekt bez wolnego poziomu? O: Nie. P: Czy darowizna jest na stronie I? O: Nie.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Akcja Sponsorzy',
    content: [
      h('Co robią sponsorzy', 2),
      p(
        'Zagrywasz kartę sponsora, której poziom nie przekracza dozwolonego limitu siły. Sponsor może dawać efekt natychmiastowy, stałą zdolność, dochód w przerwie, punktację końcową albo unikatowy budynek. Alternatywnie — zamiast zagrania — możesz przesunąć przerwę i wziąć monety według tabeli.'
      ),
      h('Tabela sił', 2),
      list([
        'Strona I: zagraj dokładnie 1 sponsora z ręki o poziomie ≤ siła albo przesuń przerwę o wartość siły i weź tyle samo monet.',
        'Strona II: zagraj jednego lub więcej sponsorów z ręki i/lub wystawki w zasięgu, gdy suma poziomów ≤ siła + 1; albo przerwa o wartość siły i 2× siła monet.',
        'Za sponsora z wystawki (II) dopłać numer folderu. Pełną kwotę z alternatywy przerwy bierzesz także, gdy znacznik wcześniej dotrze do końca toru. Siła >5 nadal zwiększa limit sumy poziomów na II.',
      ]),
      h('Pełny przykład', 2),
      p(
        'Siła 4 II: możesz zagrać sponsorów o sumie poziomów do 5 (np. 2+3) z ręki/zasięgu, płacąc też foldery za karty z wystawki, albo przesunąć przerwę o 4 i wziąć 8 monet. Siła 4 I: jeden sponsor poziomu ≤4 albo przerwa +4 i 4 monety.'
      ),
      warning(
        'Typowy błąd: użycie niezweryfikowanego przelicznika zamiast tabeli, zagranie sponsora ponad limit sumy albo pominięcie dopłaty folderu.'
      ),
      tip(
        'Wyjątek i odsyłacz: tekst efektu sponsora zawsze pochodzi z karty. Unikatowy budynek stawia się według reguł budowy i tekstu karty — patrz „Akcja Budowa”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Sponsor ≤ limit albo przerwa+monety; II sumuje poziomy do siła+1. P: Czy muszę zagrać sponsora? O: Nie — możesz wybrać alternatywę przerwy. P: Czy II pozwala brać spoza zasięgu? O: Nie, bez efektu omijającego zasięg.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Akcja zastępcza: żeton X',
    content: [
      h('Kiedy i po co', 2),
      p(
        'Gdy nie możesz lub nie chcesz wykonać tekstu wybranej akcji, zamiast niej bierzesz dokładnie 1 żeton X. To legalna „pusta” tura, która i tak przesuwa kartę na pozycję 1 — przydatna przy limicie 5 X albo gdy potrzebujesz tylko przestawić kolejność kart.'
      ),
      list([
        'Wybierz dowolną kartę akcji.',
        'Nie wykonuj jej zwykłego efektu.',
        'Przenieś ją na pozycję 1 w normalny sposób.',
        'Weź dokładnie 1 żeton X.',
      ]),
      h('Przykład', 2),
      p(
        'Masz 4 żetony X i Karty na 5, ale nie chcesz dobierać przed przerwą. Wybierasz Karty jako akcję zastępczą, przesuwasz je na 1 i bierzesz piąty X. Nie wolno wybrać tej akcji, gdy masz już 5 X.'
      ),
      warning(
        'Typowy błąd: wykonać część zwykłej akcji i dodatkowo wziąć X albo użyć zastępczej przy pełnym limicie 5.'
      ),
      tip(
        'Wyjątek: siła pozycji i strona I/II nie wpływają na akcję zastępczą. Wydawanie X przed zwykłą akcją opisuje „Najważniejsze pojęcia” i „Przebieg tury”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Zastępcza = przesunięcie karty + 1 X, bez efektu. P: Czy mogę wziąć 2 X? O: Nie, dokładnie 1. P: Czy przesuwa przerwę jak Karty? O: Nie — nie wykonujesz tekstu Kart.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Rodzaje efektów kart',
    content: [
      h('Jak czytać kartę zoo', 2),
      p(
        'Karty zoo (zwierzęta, sponsorzy, projekty, punktacja) mają różne momenty działania. Rozpoznanie typu efektu zapobiega „zapomniane w przerwie” i „punktowane za wcześnie”.'
      ),
      list([
        'Natychmiastowe — przy zagrywaniu karty.',
        'Stałe — od zagrania, dopóki karta jest w zoo.',
        'Dochodowe — w każdej przerwie.',
        'Punktacja końcowa — po zakończeniu gry.',
        'Interakcyjne — wobec innych graczy; w solo stosuj niebieski wariant / reguły solo.',
      ]),
      h('Kolejność', 2),
      p(
        'Gdy kilka twoich efektów uruchamia się jednocześnie, wybierasz kolejność, chyba że szczegółowa zasada mówi inaczej. Ukończ jeden efekt przed następnym. Tekst konkretnej karty i oficjalny glosariusz mają pierwszeństwo przed ogólnym opisem.'
      ),
      h('Przykład', 2),
      p(
        'Zagrywasz zwierzę z atrakcyjnością i zdolnością natychmiastową oraz masz stały efekt sponsora reagujący na nowe zwierzę. Najpierw domykasz procedurę zagrania (wybieg, koszt, atrakcyjność), potem zdolność, potem ewentualne triggery stałe — albo w innej legalnej kolejności, jeśli tekst na to pozwala, ale zawsze kończysz jeden efekt przed kolejnym.'
      ),
      warning(
        'Typowy błąd: mieszać dochód przerwy z efektem natychmiastowym albo stosować efekt interakcyjny w solo bez wariantu solo.'
      ),
      tip(
        'Odsyłacz: słowa kluczowe zwierząt mają własny rozdział; Rafa i zwierzęta morskie — w rozdziale dodatku Zwierzęta Wodne.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Moment efektu wynika z typu; przy remisie kolejności decydujesz ty. P: Czy stały efekt działa po usunięciu karty? O: Nie. P: Co wygrywa konflikt tekstów? O: Karta / glosariusz nad ogólną regułą.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Najczęstsze słowa kluczowe zwierząt',
    content: [
      h('Jak używać tego rozdziału', 2),
      p(
        'Poniższe definicje opisują standardowe działanie. Przy różnicy wygrywa tekst karty i oficjalny glosariusz. Nie zgaduj liczb — wartości biorą się z karty.'
      ),
      h('Determinacja', 2),
      p(
        'Umieść wybraną kartę akcji na pozycji 1, przesuwając pozostałe zgodnie z ikoną. Manipuluje kolejnością, ale nie wykonuje dodatkowej akcji.'
      ),
      h('Inteligencja', 2),
      p(
        'Otrzymujesz żetony X albo efekt związany z pozycjami kart — dokładnie według wartości na karcie.'
      ),
      h('Skok', 2),
      p(
        'Przesuń znacznik przerwy o wskazaną liczbę pól i weź korzyść, jeżeli zdolność ją przewiduje.'
      ),
      h('Chwytanie', 2),
      p(
        'Dobierasz kartę albo korzystasz z wystawki według wartości zdolności i zasad zasięgu reputacji.'
      ),
      h('Polowanie', 2),
      p(
        'Odkrywasz karty z talii i szukasz właściwego rodzaju. Nieprzyjęte trafiają na odrzucone w kolejności z opisu zdolności.'
      ),
      h('Jadowitość i duszenie', 2),
      p(
        'Jadowitość kładzie żeton jadu na karcie akcji przeciwnika; wpływa na najbliższe użycie i znika w przerwie, jeśli niezużyty. Duszenie kładzie żeton ograniczenia według zasady zdolności; znika po zadziałaniu albo w przerwie.'
      ),
      h('Małpy / kradzież', 2),
      p(
        'Efekty interakcyjne wobec dozwolonych przeciwników. Nie zabierasz zasobu, którego nie ma. W solo — efekt alternatywny.'
      ),
      warning(
        'Typowy błąd: potraktować Determinację jako drugą pełną akcję albo pominąć wariant solo przy interakcji.'
      ),
      tip(
        'Odsyłacz: timing efektów — „Rodzaje efektów kart”; przesuwanie kart — „Przebieg tury”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Słowa kluczowe = skróty procedur; liczby z karty. P: Czy Determinacja daje dodatkową akcję? O: Nie. P: Czy jad znika od razu po położeniu? O: Nie — po użyciu lub w przerwie zgodnie z ikoną.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Przerwa',
    content: [
      h('Kiedy i dlaczego', 2),
      p(
        'Przerwę uruchamia dojście znacznika do końca toru. Gracz wywołujący kończy bieżącą turę, potem wszyscy wykonują kroki poniżej. Przerwa nie zaczyna „nowej rundy” — rund nie ma; gra wraca do następnego gracza w kolejności.'
      ),
      checklist(
        'core-break',
        [
          'Limit ręki: odrzuć nadmiar kart (zwykle 3; 5 po odpowiedniej uczelni).',
          'Usuń z kart akcji żetony czasowe wskazane przez reguły (w tym niewykorzystane żetony interakcyjne znikające w przerwie).',
          'Wszyscy odzyskują pracowników; uzupełnij ogrody partnerskie i uczelnie zgodnie z planszą.',
          'Odśwież wystawkę: odrzuć dwie najniższe karty, przesuń pozostałe, uzupełnij z talii.',
          'Dochód: tor atrakcyjności, kioski, sponsorzy, pola mapy i inne źródła z ikoną dochodu.',
          'Ustaw znacznik przerwy na polu startowym dla liczby graczy.',
          'Kontynuuj od następnego gracza — przerwa nie zaczyna nowej rundy (rund nie ma).',
        ],
        'Przerwa'
      ),
      diagram(
        'ark-break',
        'Diagram kolejności przerwy',
        'Limit ręki → żetony czasowe → pracownicy i plansza Stowarzyszenia → wystawka → dochód → reset toru przerwy.'
      ),
      h('Dochód — przykład', 2),
      p(
        'Wywołałeś przerwę mając 7 kart i limit 3: najpierw odrzucasz 4. Potem wracają pracownicy. Wystawka: dwie najniższe na odrzucone, reszta w dół, uzupełnienie. Dochód: odczyt z toru atrakcyjności + każdy kiosk osobno + sponsorzy z ikoną dochodu. Znacznik przerwy wraca na start dla waszej liczby graczy; kolejny gracz po tobie zaczyna turę.'
      ),
      warning(
        'Typowy błąd: odrzucać rękę w trakcie tury, zaczynać „rundę” od wywołującego przerwę albo liczyć pusty wybieg przy kiosku.'
      ),
      tip(
        'Wyjątek: gdy kolejność dochodu ma znaczenie, zaczynacie od wywołującego przerwę; własne efekty dochodowe rozpatrujesz w wybranej kolejności.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Limit → żetony → pracownicy → wystawka → dochód → reset. P: Czy wywołujący gra od razu następną turę? O: Nie automatycznie. P: Ile kart odrzucam z wystawki? O: Dwie najniższe.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Tory i premie',
    content: [
      h('Trzy tory, trzy role', 2),
      p(
        'Atrakcyjność karmi dochód i zbliża znacznik od jednej strony. Ochrona przesuwa drugi znacznik i daje jednorazowe premie pól. Reputacja otwiera foldery wystawki i ma własne progi. Razem budują wynik i tempo końca gry.'
      ),
      h('Atrakcyjność', 2),
      p(
        'Zwiększa dochód w przerwie, zbliża znacznik do ochrony i może dawać premie pól/efektów. Wartości dochodu odczytuj z planszy przy swoim znaczniku.'
      ),
      h('Ochrona przyrody', 2),
      p(
        'Na kluczowych polach bierzesz jednorazowe premie, np. ulepszenie karty lub dodatkowego pracownika, pieniądze lub żeton premii, albo wybór jednej z dwóch kart punktacji końcowej po progu. Gdy pierwszy gracz osiągnie próg wyboru karty końcowej, wszyscy wybierają jedną z dwóch i odrzucają drugą. Jeśli nikt nie osiągnie progu przed końcem, wybór następuje przed punktacją końcową.'
      ),
      h('Reputacja i wystawka', 2),
      list([
        '0–1: folder 1; 2–4: 1–2; 5–7: 1–3; 8–9: 1–4; 10–14: 1–5; 15: 1–6.',
        'Wejście na 10+ wymaga Kart II.',
        'Na 15 znacznik zostaje; każda dalsza reputacja → 1 atrakcyjność.',
        'Zwierzęta II / Sponsorzy II / projekt przez Stowarzyszenie II: dopłata = numer folderu.',
      ]),
      warning(
        'Blocker: dokładne premie nadrukowane na torze reputacji (w tym pole 5) pozostają referencją do fizycznej planszy — lokalne zdjęcie nie pozwala ich przepisać bez zgadywania. Nie zgaduj symboli.'
      ),
      h('Przykład zasięgu', 2),
      p(
        'Reputacja 8: foldery 1–4. Masz Zwierzęta II, ale reputację 4: folder 3 jest poza zasięgiem — samo ulepszenie nie omija reputacji. Przy 15 dalsza reputacja z efektu daje atrakcyjność zamiast przesunięcia znacznika.'
      ),
      tip(
        'Odsyłacz: zgarnięcie z akcji Kart omija zasięg. Diagram zasięgu jest w „Akcja Karty”. Koniec gry — następny rozdział.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Atrakcyjność = dochód; ochrona = premie i drugi znacznik; reputacja = foldery. P: Czy mogę wejść na 10 bez Kart II? O: Nie. P: Czy aplikacja zna premię pola 5 reputacji? O: Nie — patrz plansza.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Koniec gry',
    content: [
      h('Kiedy kończy się partia', 2),
      p(
        'Na końcu tury albo w przerwie sprawdź, czy atrakcyjność i ochrona dowolnego gracza są w tym samym obszarze punktowym albo minęły się. To trigger — nie natychmiastowy koniec bez ostatnich tur.'
      ),
      checklist(
        'core-endgame',
        [
          'Gracz aktywny kończy całą swoją turę.',
          'Pozostali gracze otrzymują po jednej ostatniej turze (równa liczba tur).',
          'Nie przeprowadza się dodatkowych pełnych rund.',
          'Ustal bazową wartość z położenia znaczników atrakcyjności i ochrony.',
          'Dodaj lub odejmij punkty z zachowanej karty punktacji końcowej.',
          'Dodaj punkty końcowe z kart sponsorów, zwierząt, mapy i innych efektów.',
          'Zastosuj wszystkie kary.',
          'Gracz z najwyższym wynikiem wygrywa.',
        ],
        'Koniec gry'
      ),
      diagram(
        'ark-end-scoring',
        'Diagram końca gry i punktowania',
        'Skrzyżowanie znaczników → ostatnie tury → odczyt obszaru punktowego z planszy → karta końcowa, efekty i kary.'
      ),
      h('Remis', 2),
      p(
        'Wygrywa remisujący, który wsparł najwięcej projektów ochrony (policz znaczniki zdjęte z lewej strony jego mapy zoo). Jeśli nadal remis — remisujący wspólnie wygrywają.'
      ),
      h('Przykład', 2),
      p(
        'W twojej turze znaczniki skrzyżowały się. Kończysz turę. Każdy przeciwnik ma jeszcze jedną turę. Potem odczytujecie obszar punktowy z planszy, dodajecie kartę końcową i efekty, odejmujecie kary. Kontrprzykład: ogłoszenie zwycięzcy w chwili skrzyżowania bez ostatnich tur.'
      ),
      warning(
        'Typowy błąd: natychmiastowy koniec bez równych tur albo odgadywanie wartości obszaru zamiast odczytu z planszy.'
      ),
      tip(
        'Wartość obszaru punktowego to odczyt z planszy, nie stała tabela aplikacji. Ekran punktowania powtarza remis i przykład.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Skrzyżowanie → ostatnie tury → punktacja → remis przez projekty. P: Czy są dodatkowe rundy? O: Nie. P: Co przy podwójnym remisie? O: Wspólne zwycięstwo.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Gra solo',
    content: [
      h('Co zmienia solo', 2),
      p(
        'W solo nie używasz zwykłego toru przerwy. Zamiast niego działa tor czasu solo z siedmioma znacznikami. Celem jest jak najwyższy wynik przed wyczerpaniem czasu.'
      ),
      list([
        'Przygotuj trzy bazowe projekty ochrony.',
        'Rozpocznij z dodatkową atrakcyjnością wskazaną w zasadach solo.',
        'Po każdej swojej turze przesuń kolejny znacznik czasu.',
        'Po określonej liczbie tur następuje przerwa.',
        'Podczas przerwy jeden znacznik trafia na najtańsze wolne pole darowizny, skracając dalszą grę.',
        'Efekty interakcyjne stosują wariant solo z niebieskiego paska karty.',
      ]),
      warning(
        'Typowy błąd: grać solo na zwykłym torze przerwy wieloosobowej albo stosować czerwone efekty interakcyjne zamiast niebieskiego wariantu.'
      ),
      tip(
        'Szczegółowe progi atrakcyjności startowej i liczby tur między przerwami odczytaj z oficjalnych zasad solo / komponentów solo — aplikacja nie zgaduje brakujących liczb spoza lokalnego materiału.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Solo = tor czasu + warianty interakcji + darowizna skracająca grę. P: Czy są przeciwnicy? O: Nie w standardowym solo. P: Czy przerwa działa jak w multi? O: Tak w krokach limitu/dochodu, z dodatkiem znacznika na darowiznę.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Pierwsze tury — przewodnik',
    content: [
      h('Dlaczego ten schemat', 2),
      p(
        'To nie obowiązkowa strategia, lecz bezpieczna ścieżka nauki obu torów wyniku bez tonięcia w wyjątkach map asymetrycznych.'
      ),
      h('Przed pierwszą turą', 2),
      list([
        'Jakie zwierzęta masz na ręce i jaki wybieg im potrzebny.',
        'Ile kosztują i jakie mają wymagania.',
        'Czy na wystawce jest łatwa karta.',
        'Które projekty ochrony są dostępne.',
      ]),
      h('Typowy początek', 2),
      list([
        'Zbuduj tani wybieg pasujący do zwierzęcia na ręce.',
        'Zagraj zwierzę dające atrakcyjność i prostą zdolność.',
        'Użyj Stowarzyszenia po ogród partnerski albo uczelnię.',
        'Pilnuj gotówki przed kolejnym zwierzęciem.',
        'Nie zagrywaj wszystkich kart „bo są na ręce”.',
        'W pierwszej połowie wesprzyj co najmniej jeden projekt ochrony.',
        'Ulepszaj akcje pod plan, nie wszystkie „najsilniejsze” automatycznie.',
      ]),
      warning(
        'Typowy błąd: rozwijać tylko atrakcyjność albo startować na mapie asymetrycznej przy pierwszej nauce.'
      ),
      tip(
        'Odsyłacze: Budowa → Zwierzęta → Stowarzyszenie → Przerwa. Przy dodatku najpierw przeczytaj rozdział Zwierzęta Wodne.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Wybieg → zwierzę → stowarzyszenie → ochrona. P: Czy muszę grać dokładnie tak? O: Nie; to tylko szkolenie. P: Kiedy ulepszać Karty? O: Gdy planujesz reputację 10+ lub mocne dobieranie.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Lista kontrolna tury',
    content: [
      h('Przed akcją', 2),
      checklist(
        'core-turn-checklist-before',
        [
          'Jaka jest siła karty?',
          'Czy opłaca się wydać żeton X?',
          'Czy spełniam wymagania?',
          'Czy mam pieniądze i miejsce?',
          'Czy akcja przesunie przerwę?',
          'Czy grozi mi przekroczenie limitu ręki przy nadchodzącej przerwie?',
        ],
        'Przed akcją'
      ),
      h('Po akcji', 2),
      checklist(
        'core-turn-checklist-after',
        [
          'Czy karta akcji trafiła na pozycję 1?',
          'Czy przesunąłem pozostałe karty?',
          'Czy otrzymałem atrakcyjność, reputację i ochronę?',
          'Czy rozpatrzyłem zdolność karty?',
          'Czy uzupełniłem wystawkę?',
          'Czy sprawdziłem przerwę?',
          'Czy sprawdziłem koniec gry?',
        ],
        'Po akcji'
      ),
      tip(
        'Trzymaj tę listę obok stołu przez pierwsze partie. Pełne procedury są w rozdziałach akcji i „Przerwa”.'
      ),
      h('Podsumowanie i mikro-FAQ', 2),
      p(
        'Pytania przed chronią przed nielegalną akcją; pytania po — przed pominiętym przesunięciem. P: Czy X wydaję po efekcie? O: Nie, przed. P: Czy limit ręki jest tu? O: Tylko ostrzeżenie przed nadchodzącą przerwą.'
      ),
      p('---'),
    ],
  },
  {
    title: 'Najczęstsze błędy',
    content: [
      h('Szybkie odpowiedzi dla stołu', 2),
      list([
        'Zagrywanie zwierzęcia bez pustego odpowiedniego wybiegu.',
        'Liczenie ikony zagrywanej karty do jej własnego wymagania.',
        'Zapominanie o przesunięciu karty akcji na pozycję 1.',
        'Wydawanie żetonu X po rozpoczęciu rozpatrywania akcji.',
        'Sprawdzanie limitu ręki po każdej turze zamiast podczas przerwy.',
        'Otrzymywanie dochodu za pusty wybieg obok kiosku.',
        'Traktowanie gry jak gry rundowej.',
        'Zapominanie o zwrocie pracowników podczas przerwy.',
        'Wspieranie projektu bez wolnego poziomu.',
        'Zagrywanie projektu z ręki bez możliwości natychmiastowego wsparcia.',
        'Stosowanie zniżki ogrodu partnerskiego do niepasującego kontynentu.',
        'Przesuwanie budynków po ich postawieniu.',
        'Pobieranie karty z wystawki poza zasięgiem reputacji bez specjalnego pozwolenia.',
        'Używanie zdobytego w trakcie akcji żetonu X do zwiększenia tej samej akcji.',
        'Kończenie gry natychmiast po skrzyżowaniu torów bez przyznania ostatnich tur pozostałym graczom.',
      ]),
      warning(
        'Granica kompletności: premie nadrukowane na torze reputacji nie są w aplikacji. Status treści pozostaje `partial`. Zielony test nie oznacza kompletnego katalogu kart.'
      ),
      tip(
        'Ta instrukcja jest zakończonym tekstem nauczania procedur podstawy w granicy dostępnych źródeł. Dodatek Zwierzęta Wodne ma osobny rozdział menu; 20 alternatywnych kart akcji czeka na lokalny materiał referencyjny.'
      ),
      h('Końcowe podsumowanie', 2),
      p(
        'Jedna akcja na turę, siła z pozycji, legalna budowa, wymagania przed zwierzęciem, pracownik do przerwy, limity w przerwie, koniec po równych turach. P: Czy wolno zgadywać premię reputacji z pamięci? O: Nie — patrz plansza. P: Czy można uznać grę za `ready`? O: Nie, przy znanych blockerach.'
      ),
      p('---'),
    ],
  },
];
